(() => {
  const AVAILABILITY_PARAM = 'filter.v.availability';
  const AVAILABLE = '1';
  const SOLD_OUT = '0';
  const cache = new Map();
  let requestGeneration = 0;
  let requestController = null;

  const parseSection = (html) => new DOMParser().parseFromString(html, 'text/html');

  const getPageNumber = (params) => {
    const page = Number.parseInt(params.get('page') || '1', 10);
    return Number.isFinite(page) && page > 0 ? page : 1;
  };

  const fetchSectionPage = async (baseParams, availability, page, sectionId, signal) => {
    const params = new URLSearchParams(baseParams);
    params.delete('section_id');
    params.delete('page');
    params.delete(AVAILABILITY_PARAM);
    params.append(AVAILABILITY_PARAM, availability);
    if (page > 1) params.set('page', String(page));
    params.set('section_id', sectionId);

    const url = `${window.location.pathname}?${params.toString()}`;
    if (cache.has(url)) return parseSection(cache.get(url));

    const response = await fetch(url, { signal });
    if (!response.ok) throw new Error(`Section request failed: ${response.status}`);
    const html = await response.text();
    cache.set(url, html);
    return parseSection(html);
  };

  const getStreamCount = (documentFragment) => {
    const container = documentFragment.getElementById('ProductGridContainer');
    const count = Number.parseInt(container?.dataset.productsCount || '', 10);
    if (!Number.isFinite(count) || count < 0) throw new Error('Section response is missing its product count.');
    return count;
  };

  const getCards = (documentFragment) =>
    Array.from(documentFragment.querySelectorAll('#product-grid > .grid__item'));

  const loadStreamSlice = async ({ params, availability, offset, length, pageSize, sectionId, signal }) => {
    if (length <= 0) return [];

    const firstPage = Math.floor(offset / pageSize) + 1;
    const lastPage = Math.floor((offset + length - 1) / pageSize) + 1;
    const pageNumbers = firstPage === lastPage ? [firstPage] : [firstPage, lastPage];
    const pages = await Promise.all(
      pageNumbers.map((page) => fetchSectionPage(params, availability, page, sectionId, signal))
    );

    const cards = pages.flatMap(getCards);
    return cards.slice(offset % pageSize, (offset % pageSize) + length);
  };

  const getCounts = async ({ params, requestedAvailability, nativeCount, sectionId, signal }) => {
    if (requestedAvailability.length === 1) {
      return requestedAvailability[0] === AVAILABLE
        ? { availableCount: nativeCount, soldOutCount: 0 }
        : { availableCount: 0, soldOutCount: nativeCount };
    }

    const [availablePage, soldOutPage] = await Promise.all([
      fetchSectionPage(params, AVAILABLE, 1, sectionId, signal),
      fetchSectionPage(params, SOLD_OUT, 1, sectionId, signal),
    ]);

    return {
      availableCount: getStreamCount(availablePage),
      soldOutCount: getStreamCount(soldOutPage),
    };
  };

  const render = async (searchParams = window.location.search.slice(1)) => {
    requestGeneration += 1;
    const generation = requestGeneration;
    requestController?.abort();
    requestController = new AbortController();

    const container = document.getElementById('ProductGridContainer');
    const collection = container?.querySelector('.collection');
    const revealGrid = () => collection?.classList.remove('leobela-compound-sort-pending');
    const grid = container?.querySelector('#product-grid');
    if (!container || !grid?.matches('ul')) {
      revealGrid();
      return;
    }

    const params = new URLSearchParams(searchParams);

    const pageSize = Number.parseInt(container.dataset.productsPerPage || '', 10);
    const nativeCount = Number.parseInt(container.dataset.productsCount || '', 10);
    const sectionId = container.dataset.sectionId;
    if (!pageSize || !Number.isFinite(nativeCount) || !sectionId) {
      revealGrid();
      return;
    }

    const requestedAvailability = [
      ...new Set(
        params.getAll(AVAILABILITY_PARAM).filter((value) => value === AVAILABLE || value === SOLD_OUT)
      ),
    ];
    const streams = requestedAvailability.length === 1 ? requestedAvailability : [AVAILABLE, SOLD_OUT];

    try {
      const counts = await getCounts({
        params,
        requestedAvailability,
        nativeCount,
        sectionId,
        signal: requestController.signal,
      });
      const pageStart = (getPageNumber(params) - 1) * pageSize;
      const availableLength = streams.includes(AVAILABLE)
        ? Math.max(0, Math.min(pageSize, counts.availableCount - pageStart))
        : 0;
      const soldOutOffset = streams.includes(AVAILABLE) ? Math.max(0, pageStart - counts.availableCount) : pageStart;
      const soldOutLength = streams.includes(SOLD_OUT)
        ? Math.max(0, Math.min(pageSize - availableLength, counts.soldOutCount - soldOutOffset))
        : 0;

      const [availableCards, soldOutCards] = await Promise.all([
        loadStreamSlice({
          params,
          availability: AVAILABLE,
          offset: pageStart,
          length: availableLength,
          pageSize,
          sectionId,
          signal: requestController.signal,
        }),
        loadStreamSlice({
          params,
          availability: SOLD_OUT,
          offset: soldOutOffset,
          length: soldOutLength,
          pageSize,
          sectionId,
          signal: requestController.signal,
        }),
      ]);

      if (generation !== requestGeneration) return;
      const cards = [...availableCards, ...soldOutCards];
      if (cards.length !== Math.min(pageSize, nativeCount - pageStart)) {
        throw new Error('Compound page returned an unexpected number of products.');
      }

      grid.replaceChildren(...cards);
      grid.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'));
      revealGrid();
    } catch (error) {
      if (error.name !== 'AbortError' && generation === requestGeneration) {
        revealGrid();
        console.warn('[Leobela collection sort] Compound sorting failed; keeping Shopify native results.', error);
      }
    }
  };

  const cancel = () => {
    requestGeneration += 1;
    requestController?.abort();
    requestController = null;
  };

  window.LeobelaCollectionCompoundSort = Object.freeze({ cancel, render });
  document.addEventListener('DOMContentLoaded', () => render());
})();
